import { HorizontalContainer } from '@american-autonomy/ag-ui';

import Grid from 'components/Grid';
import WithField from 'components/WithField';
import SelectClientField from 'components/fields/SelectClientField';
import SelectFarmField from 'components/fields/SelectFarmField';
import CaptionText from 'components/text/CaptionText';

import useData from './hooks/useData';
import useTranslation from './hooks/useTranslation';

import { CLIENT_FIELD_NAME, FARM_FIELD_NAME, SELECT_FIELD_EMPTY_LABEL, TEST_ID } from './constants';
import type { ClientFarmSchema, FieldValues, Props } from './types';

import styles from './styles.module.scss';

const SelectClientFarmField = <T extends FieldValues = ClientFarmSchema>({
    isRequired = false,
    namePrefix,
    testId = TEST_ID
}: Props<T>) => {
    const {
        CLIENT_NAME_FIELD_PLACEHOLDER,
        FARM_NAME_FIELD_PLACEHOLDER,
        INCOMPLETE_SELECTION_WARNING,
        LABEL
    } = useTranslation();

    const {
        clientFormValue,
        containerRef,
        control,
        fieldName,
        handleChange,
        handlePanelClose,
        handlePanelOpen,
        hasIncompleteSelection,
        openPanel,
        panels
    } = useData<T>({ namePrefix });

    const incompleteWarning =
        openPanel === null && hasIncompleteSelection ? INCOMPLETE_SELECTION_WARNING : null;

    const renderPanel = () => {
        if (!openPanel) {
            return null;
        }

        const panel = panels[openPanel];
        const OpenPanel = panel.component;

        return (
            <OpenPanel
                clientId={clientFormValue?.value}
                onChange={value => handleChange(openPanel, value)}
                onClose={handlePanelClose}
                value={panel.value}
            />
        );
    };

    return (
        <Grid className={styles.grid} ref={containerRef} testId={testId}>
            <WithField className={styles.withField} label={LABEL}>
                <HorizontalContainer className={styles.horizontalContainer}>
                    <SelectClientField<T>
                        className={styles.selectField}
                        control={control}
                        isOpen={false}
                        isRequired={isRequired}
                        label={SELECT_FIELD_EMPTY_LABEL}
                        name={fieldName(CLIENT_FIELD_NAME)}
                        onPanelOpen={() => handlePanelOpen(CLIENT_FIELD_NAME)}
                        placeholder={CLIENT_NAME_FIELD_PLACEHOLDER}
                        ref={containerRef}
                    />

                    <SelectFarmField<T>
                        className={styles.selectField}
                        clientId={clientFormValue?.value}
                        control={control}
                        isOpen={false}
                        isRequired={isRequired}
                        label={SELECT_FIELD_EMPTY_LABEL}
                        name={fieldName(FARM_FIELD_NAME)}
                        onPanelOpen={() => handlePanelOpen(FARM_FIELD_NAME)}
                        placeholder={FARM_NAME_FIELD_PLACEHOLDER}
                        ref={containerRef}
                    />
                </HorizontalContainer>

                {incompleteWarning && (
                    <CaptionText className={styles.captionText} text={incompleteWarning} />
                )}
            </WithField>

            {renderPanel()}
        </Grid>
    );
};

export default SelectClientFarmField;
